import { normalizeDocumentCategory } from "./moveDocumentScope";

const SIGNABLE_EXTENSIONS = new Set(["pdf", "doc", "docx"]);

/** Documents sub-tab id for Acrobat Sign / legacy DocuSign envelope list (client details). */
export const DOC_TAB_SENT_FOR_SIGNATURE = "sent_for_signature";

export function isDocumentsFolderTab(docFileType) {
  const tab = String(docFileType || "");
  return tab !== "requested" && tab !== DOC_TAB_SENT_FOR_SIGNATURE;
}

export function isSignableDocumentName(documentName) {
  const name = String(documentName || "").trim().toLowerCase();
  if (!name.includes(".")) return false;
  const ext = name.split(".").pop();
  return SIGNABLE_EXTENSIONS.has(ext);
}

export function normalizeEmailList(value) {
  return String(value || "")
    .split(/[,;\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

export function buildDefaultSignatureSubject(documentName) {
  const title = String(documentName || "Document").trim() || "Document";
  return `Please sign: ${title}`;
}

/** Dev-only: log backend auto-detected placement from send-envelope response. */
export function logSignaturePlacementDetected(placement) {
  if (process.env.NODE_ENV === "production" || !placement) return;
  console.debug("[Acrobat Sign] signature_placement:", placement);
}

export function parseDocuSignConsentError(error) {
  const data = error?.response?.data || {};
  const bodyData = data?.data || {};
  const consentCodes = new Set([
    "ADOBE_SIGN_AUTH_REQUIRED",
    "ADOBE_SIGN_REFRESH_TOKEN_EXPIRED",
    "DOCUSIGN_CONSENT_REQUIRED",
  ]);
  const status = error?.response?.status;
  if (
    (status === 403 || status === 503) &&
    consentCodes.has(bodyData?.code) &&
    bodyData?.consent_url
  ) {
    return {
      requiresConsent: true,
      message:
        data?.message ||
        "Adobe Acrobat Sign authorization is required (refresh token may be missing or expired after 60 days of inactivity). Complete OAuth in the new tab.",
      consentUrl: bodyData.consent_url,
    };
  }
  return {
    requiresConsent: false,
    message: data?.message || error?.message || "Request failed.",
  };
}

export function openDocuSignConsentUrl(consentUrl) {
  if (!consentUrl) return;
  window.open(consentUrl, "_blank", "noopener,noreferrer");
}

const ADOBE_AUTHORIZE_FALLBACK =
  "https://app.finlotax.com/api/adobe-sign/oauth/authorize?go=1";

/** True when Adobe needs a one-time connect (missing or expired refresh token). */
export function getAdobeConnectRedirectUrl(source) {
  const body = source?.response?.data || source || {};
  const data = body?.data || {};
  const missing = Array.isArray(data.missing) ? data.missing : [];
  const needsConnect =
    missing.includes("ADOBE_SIGN_REFRESH_TOKEN") ||
    data.code === "ADOBE_SIGN_AUTH_REQUIRED" ||
    data.code === "ADOBE_SIGN_REFRESH_TOKEN_EXPIRED";
  if (!needsConnect) return null;
  const raw = data.consent_url || data.authorize_url || ADOBE_AUTHORIZE_FALLBACK;
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://app.finlotax.com${raw.startsWith("/") ? raw : `/${raw}`}`;
}

export function redirectToAdobeConnectIfNeeded(source) {
  const url = getAdobeConnectRedirectUrl(source);
  if (!url) return false;
  window.location.assign(url);
  return true;
}

export function getSignatureStatusMeta(status, recipients = []) {
  const key = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  if (key === "voided") return { label: "Voided", className: "badge-red" };
  if (key === "declined") return { label: "Declined", className: "badge-red" };
  if (key === "expired") return { label: "Expired", className: "badge-red" };
  if (key === "completed") {
    return { label: "Completed", className: "badge-green" };
  }

  const signers = (Array.isArray(recipients) ? recipients : []).filter(
    (recipient) => {
      const role = String(recipient?.role || "signer")
        .trim()
        .toLowerCase();
      return role === "signer" || !role;
    },
  );

  const signedStatuses = new Set([
    "signed",
    "completed",
    "faxpending",
    "autoresponded",
  ]);
  const signedCount = signers.filter((signer) =>
    signedStatuses.has(
      String(signer?.status || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ""),
    ),
  ).length;
  const totalSigners = signers.length;

  if (totalSigners > 0) {
    if (signedCount === 0) {
      return { label: "Sent for Signature", className: "badge-orange" };
    }
    if (signedCount < totalSigners) {
      if (signedCount === 1) {
        return { label: "1 person has signed", className: "badge-orange" };
      }
      return {
        label: `${signedCount} of ${totalSigners} signed`,
        className: "badge-orange",
      };
    }
    return { label: "Completed", className: "badge-green" };
  }

  const map = {
    created: { label: "Sent for Signature", className: "badge-orange" },
    sent: { label: "Sent for Signature", className: "badge-orange" },
    delivered: { label: "Sent for Signature", className: "badge-orange" },
    viewed: { label: "Sent for Signature", className: "badge-orange" },
    signed: { label: "Signed", className: "badge-green" },
  };

  return map[key] || { label: status || "Unknown", className: "badge-gray" };
}

export function buildSignatureSendPayload({
  document,
  clientId,
  documentCategory,
  signerName,
  signerEmail,
  additionalRecipients = [],
  ccEmails = [],
  passwordProtect = false,
  accessCode = "",
  emailSubject = "",
  emailMessage = "",
}) {
  return {
    client_id: clientId,
    client_document_id: document?.client_document_id,
    document_name: document?.document_name || document?.name || "",
    file_url: document?.fileURL || document?.file_url || "",
    document_category: documentCategory || null,
    ...(normalizeDocumentCategory(documentCategory) === "business" &&
    document?.client_business_id
      ? { client_business_id: document.client_business_id }
      : {}),
    signer_name: String(signerName || "").trim(),
    signer_email: String(signerEmail || "").trim(),
    additional_recipients: additionalRecipients
      .map((recipient) => ({
        name: String(recipient?.name || "").trim(),
        email: String(recipient?.email || "").trim(),
        role: recipient?.role === "cc" ? "cc" : "signer",
      }))
      .filter((recipient) => recipient.email),
    cc_emails: normalizeEmailList(ccEmails.join ? ccEmails.join(",") : ccEmails),
    password_protect: Boolean(passwordProtect),
    access_code: passwordProtect ? String(accessCode || "").trim() : "",
    email_subject: String(emailSubject || "").trim(),
    email_message: String(emailMessage || "").trim(),
  };
}
